/**
 * AdminWelcome Component
 * 
 * Landing page after accepting an admin invite.
 * Checks if user has admin role and redirects accordingly.
 * This page only requires 'authenticated' role, not 'admin'.
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './AdminWelcome.css';

function AdminWelcome() {
    const { user, loading, hasRole, logout } = useAuth();
    const [checking, setChecking] = useState(true);
    const [retryCount, setRetryCount] = useState(0);
    const MAX_RETRIES = 3;

    useEffect(() => {
        if (loading) return;

        // If user has admin role, redirect to admin dashboard
        if (hasRole('admin')) {
            window.location.href = '/admin';
            return;
        }

        // User is authenticated but doesn't have admin role yet
        // This can happen if the role assignment hasn't propagated
        if (retryCount < MAX_RETRIES) {
            const timer = setTimeout(() => {
                // Refresh the page to re-check roles from /.auth/me
                setRetryCount(prev => prev + 1);
                window.location.reload();
            }, 2000); // Wait 2 seconds before retry
            
            return () => clearTimeout(timer);
        }

        // After retries, stop checking
        setChecking(false);
    }, [loading, hasRole, retryCount]);

    if (loading || (checking && retryCount < MAX_RETRIES)) {
        return (
            <div className="admin-welcome">
                <div className="welcome-card">
                    <div className="spinner"></div>
                    <h1>Setting up your access...</h1>
                    <p>Please wait while we configure your admin permissions.</p>
                    {retryCount > 0 && (
                        <p className="retry-note">Checking permissions... (attempt {retryCount + 1}/{MAX_RETRIES + 1})</p>
                    )}
                </div>
            </div>
        );
    }

    // User is authenticated but doesn't have admin role
    if (user && !hasRole('admin')) {
        return (
            <div className="admin-welcome">
                <div className="welcome-card error">
                    <h1>⚠️ Access Pending</h1>
                    <p className="user-info">Signed in as: <strong>{user.userDetails}</strong></p>
                    
                    <div className="message">
                        <p>
                            Your account has been authenticated, but admin access hasn't been granted yet.
                        </p>
                        <p>
                            This can happen if:
                        </p>
                        <ul>
                            <li>The role invitation is still being processed (try refreshing in a minute)</li>
                            <li>You need to be added as an admin by an existing administrator</li>
                            <li>You signed in with a different account than the one that was invited</li>
                        </ul>
                    </div>

                    <div className="actions">
                        <button 
                            className="btn-primary"
                            onClick={() => window.location.reload()}
                        >
                            🔄 Refresh & Check Again
                        </button>
                        <button 
                            className="btn-secondary"
                            onClick={() => logout('/admin/login')}
                        >
                            Sign Out & Try Different Account
                        </button>
                    </div>

                    <p className="help-text">
                        If the issue persists, please contact an existing administrator to verify your role assignment.
                    </p>
                </div>
            </div>
        );
    }

    // Not authenticated at all (shouldn't happen with route config, but handle it)
    return (
        <div className="admin-welcome">
            <div className="welcome-card">
                <h1>Authentication Required</h1>
                <p>Please sign in to continue.</p>
                <button 
                    className="btn-primary"
                    onClick={() => window.location.href = '/.auth/login/aad?post_login_redirect_uri=/admin/welcome'}
                >
                    Sign In with Microsoft
                </button>
            </div>
        </div>
    );
}

export default AdminWelcome;
